import React from "react";

import DateRangeBar from "components/DateRangeBar";
import PageContent from "components/PageContent";
import Panel from "components/Panel";
import { useDateRange } from "components/WithDateRange";
import { useQueryStringSearch } from "components/QueryStringSearch";

import { useUserPreferences } from "utils/hooks";
import { UserPreferences } from "utils/preferences";

import QueryGraph from "./QueryGraph";
import QueryTable from "./QueryTable";
import styles from "./style.module.scss";
import { useCurrentServerGroup } from "components/WithCurrentOrganization";
import Tip from "components/Tip";
import { formatList } from "utils/format";

function ServerGroupPerDatabaseQueryPerformance({
  datname,
}: {
  datname: string;
}) {
  const serverGroup = useCurrentServerGroup();
  const [qlPrefs, setQlPrefs] = useUserPreferences("queryList");
  const mergeQlPrefs = (newValues: Partial<UserPreferences["queryList"]>) => {
    setQlPrefs({ ...qlPrefs, ...newValues });
  };
  const setShowRead = (showRead: boolean) => {
    mergeQlPrefs({ showRead });
  };
  const setShowWrite = (showWrite: boolean) => {
    mergeQlPrefs({ showWrite });
  };
  const setShowOther = (showOther: boolean) => {
    mergeQlPrefs({ showOther });
  };
  const setDoCompare = (doCompare: boolean) => {
    mergeQlPrefs({ doCompare });
  };
  const qlDefaults: UserPreferences["queryList"] = {
    showRead: true,
    showWrite: true,
    showOther: true,
    doCompare: false,
  };
  const { showRead, showWrite, showOther, doCompare } = {
    ...qlDefaults,
    ...qlPrefs,
  };

  const [searchTerm, SearchInput] = useQueryStringSearch("q");

  const { from, to } = useDateRange();

  const statementTypes: string[] = [];
  if (showRead) {
    statementTypes.push("read");
  }
  if (showWrite) {
    statementTypes.push("write");
  }
  if (showOther) {
    statementTypes.push("other");
  }

  const serverNames = serverGroup.servers.map((s) => s.name);

  return (
    <PageContent
      title={`Query Performance: ${datname}`}
      pageControls={<DateRangeBar />}
      pageCategory="queries"
      pageName="index_server_group_per_database"
    >
      <Panel
        title={
          <span>
            Queries in {datname} on members of {serverGroup.name}{" "}
            <Tip
              content={`Includes combined stats from servers ${formatList(
                serverNames,
              )}`}
            />
          </span>
        }
      >
        <QueryGraph serverGroupId={serverGroup.id} datname={datname} />
        <form
          className={styles.queryFilter}
          onSubmit={(evt) => evt.preventDefault()}
        >
          <div className="flex">
            <input
              type="checkbox"
              checked={showRead}
              name="filter[statement_type][read]"
              id="filter_statement_type_read"
              onChange={(evt) => setShowRead(evt.target.checked)}
            />
            <label htmlFor="filter_statement_type_read"> SELECT</label>

            <input
              type="checkbox"
              checked={showWrite}
              id="filter_statement_type_write"
              onChange={(evt) => setShowWrite(evt.target.checked)}
            />
            <label htmlFor="filter_statement_type_write">
              {" "}
              INSERT, UPDATE, DELETE
            </label>

            <input
              type="checkbox"
              checked={showOther}
              id="filter_statement_type_other"
              onChange={(evt) => setShowOther(evt.target.checked)}
            />
            <label htmlFor="filter_statement_type_other">
              {" "}
              DDL &amp; other
            </label>
          </div>
          <div className="grow shrink-0 basis-auto" />
          <div className="flex">
            <input
              type="checkbox"
              checked={doCompare}
              id="include_comparison"
              onChange={(evt) => setDoCompare(evt.target.checked)}
            />
            <label htmlFor="include_comparison"> Compare to 7 days ago</label>
          </div>
          <SearchInput name="filter[search]" debounceMs={300} />
        </form>
        {from && to && (
          <QueryTable
            searchTerm={searchTerm}
            serverGroupId={serverGroup.id}
            datname={datname}
            compare={doCompare}
            startTs={from.unix()}
            endTs={to.unix()}
            statementTypes={statementTypes}
          />
        )}
      </Panel>
    </PageContent>
  );
}

export default ServerGroupPerDatabaseQueryPerformance;
